// @flow
import React, { Fragment, Component } from 'react';
import { Redirect } from 'react-router-dom';
import { withHelmet } from 'infrastructure/seo/Helmet';
import ContentBloc from 'components/newsletter/blocs/ContentBloc';
import { STATUS_LOADING, STATUS_DEFAULT, STATUS_UNKNOWN, STATUS_SUCCESS } from 'constants/statusConstants';
import Loading from 'components/fragments/Loading';
import Download from 'containers/fragments/DownloadContainer';
import MemberPageDetail from 'containers/page/MemberPageDetailContainer';
import { DOCUMENTS_PER_PAGE } from 'constants/pageConstants';
import { SORT_NAME, SORT_DATE } from 'constants/sortConstants';
import DropdownRadioInput from 'components/fragments/input/DropdownRadioInput';
import {
  sortDocumentsByName,
  sortDocumentByMonth,
  sortDocumentsByDate,
  getSeasonFromDocuments,
  getTypesFromDocuments
} from 'utils/documentsUtils';
import { getMonthName } from 'utils/dateUtils';
import PageNotFound from 'components/app/PageNotFound';
import JobListContainer from 'containers/job/JobListContainer';
import PageHubEntrepriseOffre from 'containers/page/template/PageHubEntrepriseOffreContainer';
import PageHubAccreditations from 'components/page/template/PageHubAccreditations';

import type { Match } from 'react-router-dom';
import type { Status } from 'types/Status';
import type { BlocContent } from 'types/NewsletterBloc';
import type { DocumentType, MemberType, DepartmentType, ArbitreType } from 'types/PageDetail';
import type { AcademieType } from 'types/Academie';
import AcademieListing from './AcademieListing';

export type OwnProps = {
  location: {
    pathname: string,
    search: string
  }
};

export type DispatchProps = {
  fetchPageDetailPreview: (id: number) => void
};

export type StateProps = {
  status: Status,
  title: string,
  content: Array<BlocContent>,
  documents: Array<DocumentType>,
  membersGroups: Array<{
    name: string,
    child: Array<MemberType>
  }>,
  liste_enfants: Array<any>,
  contentNode: Array<BlocContent>,
  departmentsGroups: Array<DepartmentType>,
  arbitresGroups: Array<ArbitreType>,
  academies: Array<AcademieType>,
  meta_description: string,
  meta_title: string,
  template: string,
  redirection: string
};

type HistoryProps = {
  history: any
};

type Props = {
  pageSlug: string,
  match: Match
} & OwnProps &
  DispatchProps &
  StateProps &
  HistoryProps;

type State = {
  displayedDocuments: number,
  sort: SORT_NAME | SORT_DATE | { from: Date, to: Date },
  typeDocument: string,
  currentPage: string
};

class PageDetail extends Component<Props, State> {
  _pageContent: ?HTMLElement;
  _links: Array<any>;
  props: Props;
  state: State = {
    currentPage: '',
    displayedDocuments: DOCUMENTS_PER_PAGE,
    sort: SORT_DATE,
    typeDocument: ''
  };

  componentDidMount() {
    /**
     * On peut être en mode prévisualisation, le tester
     */
    const splits = this.props.location.pathname.split('/');

    if (/page_/.test(splits[splits.length - 1])) {
      const id = splits[splits.length - 1].replace('page_', '');
      this.props.fetchPageDetailPreview(parseInt(id, 10));
    }

    this.setState({ currentPage: 'ok' });
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    if (nextState !== this.state) {
      return true;
    } else if (nextProps !== this.props) {
      if (nextProps.location.search !== this.props.location.search) {
        return false;
      }
      return true;
    }
    return false;
  }

  handleSortChanged = (sort: SORT_NAME | { start: Date, end: Date }) => {
    this.setState({ sort });
  };

  handleTypeChanged = (typeDocument: string) => {
    this.setState({ typeDocument });
  };

  handleShowMoreDocuments = (e: MouseEvent) => {
    e.preventDefault();
    this.setState({ displayedDocuments: this.state.displayedDocuments + DOCUMENTS_PER_PAGE });
  };

  renderDetail = () => {
    const { content } = this.props;
    return content.map((blocFigure, index) => {
      return <ContentBloc key={index} {...blocFigure} />;
    });
  };

  render() {
    const {
      status,
      template,
      documents,
      membersGroups,
      departmentsGroups,
      arbitresGroups,
      academies,
      contentNode,
      liste_enfants,
      title,
      redirection
    } = this.props;
    let preview = false;
    const splits = this.props.location.pathname.split('/');
    if (/page_/.test(splits[splits.length - 1])) {
      preview = true;
    }
    if (!preview && (status === STATUS_LOADING || status === STATUS_DEFAULT)) {
      if (template && template === 'page-hub_entreprises_salons_seminaires') {
        return <Loading />;
      }
      return <Loading isWhite />;
    }
    if (status === STATUS_UNKNOWN) {
      return <PageNotFound />;
    }

    if (this.state.currentPage === 'ok' && status === STATUS_SUCCESS && redirection) {
      return <Redirect to={{ pathname: redirection, state: { redirection: false } }} />;
    }

    switch (template) {
      case 'page-emplois_liste_offre':
        return <JobListContainer />;
      case 'page-academies_rugby':
        return <AcademieListing academies={academies} />;
      case 'page-academies_poles_espoirs':
        return <AcademieListing academies={academies} />;
      case 'page-hub_entreprises_salons_seminaires':
        return <PageHubEntrepriseOffre liste_enfants={liste_enfants} content={contentNode} />;
      case 'page-accreditations_presse':
        return <PageHubAccreditations content={contentNode} />;
      default:
        return (
          <div className="page" ref={r => (this._pageContent = r)}>
            <h2 className="mb-3">{title}</h2>
            {this.renderDetail()}
            {documents.length > 0 && this.renderDocuments()}
            {membersGroups.length > 0 && <MemberPageDetail membersGroups={membersGroups} title={title} />}
            {departmentsGroups.length > 0 && <MemberPageDetail departmentsGroups={departmentsGroups} title={title} />}
            {arbitresGroups.length > 0 && <MemberPageDetail arbitresGroups={arbitresGroups} title={title} />}
          </div>
        );
    }
  }

  renderDocuments = () => {
    const { documents } = this.props;
    const { displayedDocuments, sort } = this.state;

    const options = getSeasonFromDocuments(documents).sort((a, b) => {
      return a.label > b.label ? -1 : a.label < b.label ? 1 : 0;
    });

    const documentTypes = getTypesFromDocuments(documents).sort((a, b) => {
      const x = a.label.toLowerCase();
      const y = b.label.toLowerCase();
      return x < y ? -1 : x > y ? 1 : 0;
    });

    let length = documents.length;

    if (sort !== SORT_NAME && sort !== SORT_DATE) {
      const documentVisibles = sortDocumentByMonth(documents, sort.from, sort.to, displayedDocuments);
      length = documentVisibles.length;
    }

    return (
      <React.Fragment>
        <div className="page__head mb-3 mt-4 mt-md-7">
          <h3>Dernières publications</h3>
          {documentTypes.length > 1 && (
            <DropdownRadioInput
              id="dropdown"
              maj={true}
              title={true}
              radio={true}
              marge={false}
              packed={true}
              options={[{ value: '', label: 'Tous les documents' }, ...documentTypes]}
              inputChanged={this.handleTypeChanged}
            />
          )}
          <DropdownRadioInput
            id="dropdown"
            maj={true}
            title={true}
            radio={true}
            marge={false}
            packed={true}
            options={[
              { value: SORT_DATE, label: 'Ordre antéchronologique' },
              { value: SORT_NAME, label: 'Ordre alphabétique' },
              ...options
            ]}
            inputChanged={this.handleSortChanged}
          />
        </div>
        {this.state.sort === SORT_NAME
          ? this.renderDocumentsByName()
          : this.state.sort === SORT_DATE
          ? this.renderDocumentsByDate()
          : this.renderDocumentsBySeason(this.state.sort.from, this.state.sort.to)}
        {length > DOCUMENTS_PER_PAGE && displayedDocuments < length && (
          <a
            role="button"
            tabIndex={0}
            className="link-chevron link-chevron--block link-chevron--center link-chevron--inline link-blur"
            data-label=""
            onClick={this.handleShowMoreDocuments}
          >
            <span>Afficher plus de publications</span>
            <i className="icon icon-zoom_plus" />
          </a>
        )}
      </React.Fragment>
    );
  };

  renderDocumentsByName = () => {
    const { documents } = this.props;
    const { typeDocument } = this.state;
    let documentsFiltered = documents;
    if (typeDocument) {
      documentsFiltered = documentsFiltered.filter(document => {
        return document.tags === typeDocument;
      });
    }
    const documentsToShow = sortDocumentsByName(documentsFiltered);

    return documentsToShow
      .slice(0, this.state.displayedDocuments)
      .map((document, index) => (
        <Download
          key={`document_${document.id}`}
          title={document.title}
          date={document.date}
          label={document.tags}
          src={document.src}
        />
      ));
  };

  renderDocumentsByDate = () => {
    const { documents } = this.props;
    const { typeDocument } = this.state;
    let documentsFiltered = documents;
    if (typeDocument) {
      documentsFiltered = documentsFiltered.filter(document => {
        return document.tags === typeDocument;
      });
    }
    const documentsToShow = sortDocumentsByDate(documentsFiltered);

    return documentsToShow
      .slice(0, this.state.displayedDocuments)
      .map((document, index) => (
        <Download
          key={`document_${document.id}`}
          title={document.title}
          date={document.date}
          label={document.tags}
          src={document.src}
        />
      ));
  };

  renderDocumentsBySeason = (from: Date, to: Date): any => {
    const { documents } = this.props;
    const { typeDocument } = this.state;
    let documentsFiltered = documents;
    if (typeDocument) {
      documentsFiltered = documentsFiltered.filter(document => {
        return document.tags === typeDocument;
      });
    }
    const documentsToShow = sortDocumentByMonth(documentsFiltered, from, to, this.state.displayedDocuments);
    return documentsToShow.map((entry, index) => {
      const { documents, year, month } = entry;
      return (
        <Fragment key={`date${year}${month}`}>
          <h6>
            {getMonthName(month)} {year}
          </h6>
          {documents.map((document, index) => (
            <Download
              key={`document_${document.id}`}
              title={document.title}
              date={document.date}
              label={document.tags}
              src={document.src}
            />
          ))}
        </Fragment>
      );
    });
  };
}

export default withHelmet(PageDetail)((Helmet, pageProps) => {
  const description = `${pageProps.meta_description.slice(0, 197)}...`;

  return (
    <Helmet>
      <title>{pageProps.meta_title}</title>
      <meta name={'description'} content={description} />
      <meta property={'og:url'} content={window.location.href} />
      <meta property={'og:title'} content={pageProps.meta_title} />
      <meta property={'og:description'} content={description} />
    </Helmet>
  );
});
